import { useSelector } from 'react-redux'

import { useWatch } from 'react-hook-form'

import { DatePicker, DummyInput, Input, MultiSelect, Select, Textarea } from '@/components/Form'
import { Flex } from '@chakra-ui/react'

//API
import useUser from '@/features/users/useUser'

//Helper
import SingleSelect from '@/components/Form/Select/SingleSelect'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useCustomer } from '@/features/customers/useCustomer'
import { offerBookingGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerBookingEditRights'
import { selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'

const BookingTabGeneralInfoSection = () => {
  const offerPermissions = useSelector(selectOfferPermissions)

  const projectName = useWatch({ name: 'projectName' })

  const { useCustomerOptions } = useCustomer()
  const { useUserOptions } = useUser()

  const { data: allCustomerOptions } = useCustomerOptions({
    labelKey: (customer) => `${customer?.code ?? '(No Code)'}-${customer?.name}`,
    status: ['active', 'inactive', 'temporary', 'listed'],
  })

  //Translated

  const { data: teamAssistantOptions } = useUserOptions({
    valueKey: '_id',
    labelKey: 'fullName',
    contributorGroup: ContributorGroupType.TEAM_ASSISTANT,
  })

  const { data: engineerUserOptions } = useUserOptions({
    valueKey: '_id',
    labelKey: 'fullName',
    contributorGroup: [ContributorGroupType.ENGINEER, ContributorGroupType.TECHNICAL_PROJECT_MANAGER],
  })

  const { data: projectManagerUsersOptions } = useUserOptions({
    valueKey: '_id',
    labelKey: 'fullName',
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })

  //Permissions
  const canEditGeneralField = offerPermissions[offerBookingGroupPolicyType.BOOKING_GENERAL_INFO]?.edit ?? false
  const canEditSONumber = offerPermissions[offerBookingGroupPolicyType.BOOKING_SO_NUMBER]?.edit ?? false
  const canEditCustomerPO = offerPermissions[offerBookingGroupPolicyType.BOOKING_CUSTOMER_PO]?.edit ?? false

  const { data: allUserOptions } = useUserOptions({
    valueKey: '_id',
    labelKey: 'fullName',
  })

  return (
    <Flex direction="column" gap="2">
      <Flex gap="5">
        <DummyInput label="Project Name" value={projectName} isRow />

        <SingleSelect
          name="customer"
          label="Customer"
          disabled={true}
          options={allCustomerOptions}
          allOptions={allCustomerOptions}
          isRow
        />

        <SingleSelect
          name="endCustomer"
          label="End Customer"
          disabled={true}
          options={allCustomerOptions}
          allOptions={allCustomerOptions}
          isRow
        />
      </Flex>

      <Flex gap="5">
        <DatePicker
          name="booking.bookingStartDate"
          label="Project Start Date  (DD-MMM-YYYY)"
          disabled={!canEditGeneralField}
          isRow
        />

        <DatePicker
          name="booking.bookingEndDate"
          label="Project End Date (DD-MMM-YYYY)"
          disabled={!canEditGeneralField}
          isRow
        />
      </Flex>

      <Flex gap="5">
        <MultiSelect
          name={`engineers`}
          label="Engineers"
          options={engineerUserOptions}
          allOptions={allUserOptions}
          disabled={!canEditGeneralField}
          isRow
        />
        <Select
          name={`booking.projectManager`}
          label="Booking Project Manager"
          options={projectManagerUsersOptions}
          allOptions={allUserOptions}
          disabled={!canEditGeneralField}
          isRow
        />
        <MultiSelect
          name={`booking.teamAssistant`}
          label="Team Assistant"
          disabled={!canEditGeneralField}
          options={teamAssistantOptions}
          isRow
          isRequired={false}
        />
      </Flex>

      <Flex gap="5">
        <Input
          name="booking.SONumber"
          label="SAP Quotation#/SO # (Input by TA/ BA)"
          type="text"
          disabled={!canEditSONumber}
          isRequired={false}
          isRow
        />
      </Flex>

      <Flex gap="5">
        <Input
          name="booking.customerPONumber"
          label="Customer PO #"
          type="text"
          disabled={!canEditCustomerPO}
          isRequired={false}
          isRow
        />
        <DatePicker name="booking.PODate" label="PO Date" isRow disabled={!canEditCustomerPO} />
      </Flex>

      <Textarea
        name="booking.remark"
        label="Remark"
        placeholder=""
        disabled={!canEditGeneralField}
        rows="5"
        isRequired={false}
      />
    </Flex>
  )
}

export default BookingTabGeneralInfoSection
